import { Trans, useTranslation } from "react-i18next";

import { IPatientProtocolActionRow } from "../../../../@types/Protocol";
import { ReactComponent as ProtocolActionsEmptyImage } from "../../../../assets/images/man-reading.svg";
import { RetryButton } from "../../../../components/RetryButton";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { TableSorted } from "../../../../components/TableSorted";
import {
  ImageContainer,
  NoContentMessage,
  ProtocolActionsTableContainer,
  ProtocolActionsTableInnerContainer,
} from "./PatientProtocolActionsTable.style";
import { protocolActionsTableColumns } from "./protocolActionsTableColumns";

// EN-3346: patient-level protocol actions cannot be edited yet, so this text is
// shown on hover over each row (Scenario 3), reusing the existing hover modal.
const HOVER_TEXT_KEY = "patientPage_protocolActions_notEditable";

type PatientProtocolActionsTableProps = {
  content: IPatientProtocolActionRow[];
  isLoading?: boolean;
  isError?: boolean;
  refreshTable: () => void;
};

export function PatientProtocolActionsTable({
  content,
  isLoading,
  isError,
  refreshTable,
}: PatientProtocolActionsTableProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <ProtocolActionsTableContainer style={{ overflow: "hidden" }}>
        <ProtocolActionsTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={5} rowsNumber={8} />
        </ProtocolActionsTableInnerContainer>
      </ProtocolActionsTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <p>
          <Trans
            i18nKey="patientPage_protocolActions_loadError"
            components={{
              retry: <RetryButton onClick={refreshTable} />,
            }}
          />
        </p>
      </ImageContainer>
    );
  }

  if (content && content.length > 0) {
    return (
      <ProtocolActionsTableContainer>
        <ProtocolActionsTableInnerContainer>
          {/* Rows are intentionally not clickable in this ticket. The hover
              modal is shown per row rather than over the whole table. */}
          <TableSorted
            data={content}
            columns={protocolActionsTableColumns}
            rowTooltip={t(HOVER_TEXT_KEY)}
          />
        </ProtocolActionsTableInnerContainer>
      </ProtocolActionsTableContainer>
    );
  }

  return (
    <ImageContainer>
      <ProtocolActionsEmptyImage width={181} height={158} />
      <NoContentMessage>
        {t("patientPage_protocolActions_empty")}
      </NoContentMessage>
    </ImageContainer>
  );
}
